/**
 * TodayAppointments Widget (T051)
 * Chronological list of today's appointments
 */

import { Calendar, Clock } from 'lucide-react';
import type { DashboardData } from '../../hooks/useDashboard';

interface Props {
  data: DashboardData['today'];
}

export default function TodayAppointments({ data }: Props) {
  return (
    <div className="claude-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="text-xl font-semibold tracking-tight">Today's Appointments</h2>
        <p className="text-sm text-muted-foreground mt-1">
          {data.date &&
            new Date(data.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
        </p>
      </div>
      <div className="divide-y divide-border">
        {data.appointments.list.length === 0 ? (
          <div className="claude-empty-state">
            <Calendar className="claude-empty-state-icon" />
            <h3 className="claude-empty-state-title">No appointments today</h3>
            <p className="claude-empty-state-description">
              You have a free day ahead. Time to catch up or take a break!
            </p>
          </div>
        ) : (
          data.appointments.list.map((appointment) => (
            <div
              key={appointment.id}
              className="px-6 py-4 hover:bg-muted/30 transition-colors cursor-pointer"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0 space-y-1">
                  <p className="font-semibold text-foreground truncate">{appointment.title}</p>
                  <p className="text-sm text-muted-foreground">{appointment.customerName}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right space-y-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <p className="text-sm font-medium">
                        {new Date(appointment.startTime).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <span
                      className={`claude-badge ${
                        appointment.status === 'COMPLETED'
                          ? 'claude-badge-success'
                          : appointment.status === 'CANCELLED'
                            ? 'claude-badge-error'
                            : 'claude-badge-default'
                      }`}
                    >
                      {appointment.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
